// Zero-knowledge vault crypto: PBKDF2 -> AES-GCM key wrapping; all in the browser.
const enc = new TextEncoder();
const dec = new TextDecoder();
export const KDF_ITERATIONS = 310000;

export const toB64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
export const fromB64 = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
const randomBytes = (n) => crypto.getRandomValues(new Uint8Array(n));

export function generateRecoveryCode() {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const bytes = randomBytes(20);
  const chars = Array.from(bytes, (b) => alphabet[b % alphabet.length]);
  return [0, 5, 10, 15].map((i) => chars.slice(i, i + 5).join("")).join("-");
}

export const normalizeRecoveryCode = (code) => code.toUpperCase().replace(/[^A-Z0-9]/g, "");

async function deriveWrappingKey(secret, saltB64, iterations = KDF_ITERATIONS) {
  const base = await crypto.subtle.importKey("raw", enc.encode(secret), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: fromB64(saltB64), iterations, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["wrapKey", "unwrapKey"]
  );
}

export async function generateVaultKey() {
  return crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
}

export async function wrapVaultKey(vaultKey, secret, iterations = KDF_ITERATIONS) {
  const salt = toB64(randomBytes(16));
  const iv = randomBytes(12);
  const wrappingKey = await deriveWrappingKey(secret, salt, iterations);
  const wrapped = await crypto.subtle.wrapKey("raw", vaultKey, wrappingKey, { name: "AES-GCM", iv });
  return { salt, iv: toB64(iv), wrapped: toB64(wrapped), iterations };
}

export async function unwrapVaultKey(secret, saltB64, ivB64, wrappedB64, iterations) {
  const wrappingKey = await deriveWrappingKey(secret, saltB64, iterations);
  return crypto.subtle.unwrapKey(
    "raw", fromB64(wrappedB64), wrappingKey, { name: "AES-GCM", iv: fromB64(ivB64) },
    { name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]
  );
}

export async function encryptJson(key, obj) {
  const iv = randomBytes(12);
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, enc.encode(JSON.stringify(obj)));
  return { ciphertext: toB64(ct), iv: toB64(iv) };
}

export async function decryptJson(key, ciphertextB64, ivB64) {
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: fromB64(ivB64) }, key, fromB64(ciphertextB64));
  return JSON.parse(dec.decode(pt));
}

// Binary: 12-byte IV prefixed to ciphertext
export async function encryptBytes(key, arrayBuffer) {
  const iv = randomBytes(12);
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, arrayBuffer));
  const out = new Uint8Array(12 + ct.length);
  out.set(iv, 0);
  out.set(ct, 12);
  return out;
}

export async function decryptBytes(key, arrayBuffer) {
  const buf = new Uint8Array(arrayBuffer);
  return crypto.subtle.decrypt({ name: "AES-GCM", iv: buf.slice(0, 12) }, key, buf.slice(12));
}

export async function sha256Hex(text) {
  const digest = await crypto.subtle.digest("SHA-256", enc.encode(text));
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
}
